import pluralize from "pluralize";
import { useMemo } from "react";
import { useDispatch } from "react-redux";

import { Item, MenuOptionItem, MenuTriggerButton } from "widgets";

import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import { type ITableData, forwardToGroupRelated, getTableQuerySpecWithSelection } from "@/redux/modules/table-data.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, FEATURE_DISPLAY_NAME } from "@/settings/settings";

interface IShowRelatedButtonProps {
  table: ITableData;
}

interface IShowRelatedMenuOption {
  targetType: string;
  specificLabel?: string;
}

export function ShowRelatedButton(props: IShowRelatedButtonProps) {
  const dispatch = useDispatch<ReduxDispatch>();
  // for grouping related entities
  const innerEntityType = props.table.tableQuerySpec.resultType;
  const groupRelatedMenuItems = useMemo(() => {
    const entityType: string = props.table.tableQuerySpec.resultType;
    let menuItems: IShowRelatedMenuOption[] = [];

    // TBD: Still missing is CONTIG and PROBE
    switch (entityType) {
      case ENTITY_TYPE.GENE:
        menuItems = [
          { targetType: ENTITY_TYPE.TRANSCRIPT },
          { targetType: ENTITY_TYPE.PROTEIN },
          { targetType: ENTITY_TYPE.ORGANISM },
        ];
        break;
      case ENTITY_TYPE.PROTEIN:
      case ENTITY_TYPE.PROTEIN_LOCATION:
        menuItems = [
          { targetType: ENTITY_TYPE.GENE },
          { targetType: ENTITY_TYPE.TRANSCRIPT },
          { targetType: ENTITY_TYPE.ORGANISM },
        ];
        break;
      case ENTITY_TYPE.TRANSCRIPT:
      case ENTITY_TYPE.TRANSCRIPT_LOCATION:
        menuItems = [
          { targetType: ENTITY_TYPE.GENE },
          { targetType: ENTITY_TYPE.PROTEIN },
          { targetType: ENTITY_TYPE.ORGANISM },
        ];
        break;
      case ENTITY_TYPE.VARIANT:
      case ENTITY_TYPE.MUTATION:
        menuItems = [
          { targetType: ENTITY_TYPE.ORGANISM, specificLabel: "Mutant Genomes" },
          { targetType: ENTITY_TYPE.GENE, specificLabel: "Affected Genes" },
          { targetType: ENTITY_TYPE.PROTEIN, specificLabel: "Affected Proteins" },
        ];
        break;
      case ENTITY_TYPE.VCF_VARIANT:
        menuItems = [{ targetType: ENTITY_TYPE.ORGANISM, specificLabel: "Mutant Genomes" }];
        break;
      case ENTITY_TYPE.ORGANISM:
        menuItems = [
          { targetType: ENTITY_TYPE.GENE },
          { targetType: ENTITY_TYPE.TRANSCRIPT },
          { targetType: ENTITY_TYPE.PROTEIN },
          { targetType: ENTITY_TYPE.SAMPLE },
        ];
        break;
      case ENTITY_TYPE.SAMPLE:
        menuItems = [{ targetType: ENTITY_TYPE.ORGANISM }];
        break;
    }

    return menuItems;
  }, [props.table.tableQuerySpec.resultType]);

  if (!groupRelatedMenuItems.length) {
    return null;
  }

  const tooltipTextOfGroupRelated =
    "Show a table of items related to the selected " +
    pluralize.plural(FEATURE_DISPLAY_NAME[innerEntityType]).toLowerCase();

  return (
    <MenuTriggerButton
      icon={"share"}
      tooltip={tooltipTextOfGroupRelated}
      label={"Show Related"}
      showLabel
      onAction={(key) => {
        const target = groupRelatedMenuItems.filter((i) => i.targetType === key)?.[0];

        const targetTypeLabel =
          target.specificLabel || capitalizeFirstLetter(pluralize.plural(FEATURE_DISPLAY_NAME[target.targetType]));

        dispatch(
          forwardToGroupRelated({
            targetType: target.targetType,
            tableQuerySpec: getTableQuerySpecWithSelection(props.table),
            groupName: `Related ${targetTypeLabel}`,
          }),
        );
      }}
    >
      {groupRelatedMenuItems.map((menuItem) => {
        const targetTypeLabel =
          menuItem.specificLabel || capitalizeFirstLetter(pluralize.plural(FEATURE_DISPLAY_NAME[menuItem.targetType]));
        // const selectionPhrase = hasSelection(props.table) ? "of the selected rows " : "";

        return (
          <Item key={menuItem.targetType}>
            <MenuOptionItem
              id={targetTypeLabel}
              title={targetTypeLabel}
              // description={`Show the related ${targetTypeLabel} ${selectionPhrase}in a separate table.`}
              icon={"table"}
            />
          </Item>
        );
      })}
    </MenuTriggerButton>
  );
}
